/**
 * Snowflake JSON rewriters. Lifted out of `apollo/index.ts` so they can be
 * unit-tested with zero `@apollo/client` import (Node's built-in test runner
 * covers them directly — no Jest/Vitest dep needed).
 *
 * Discord guild / user / message ids are 64-bit unsigned ints. JS `Number`
 * can only represent values up to `Number.MAX_SAFE_INTEGER = 2^53 - 1` —
 * today's Discord snowflakes are already ≥ 2^60, so a `JSON.parse` round-trip
 * silently truncates the last digits. We keep them end-to-end as strings
 * (see `snowflake_serde` in `apps/backend/api/src/auth.rs`) and rewrite the
 * raw JSON body in both directions so the truncation never happens.
 *
 * Keep this list in sync with `operations.graphql` and `auth.rs`:
 *   - `discordGuildId` (response field on every node of `MyGuilds.myGuilds`
 *     — populates `availableGuilds` in the store)
 *   - `guildId`        (request var for `Board`, `GuildMembers`, every
 *     board-scoped mutation, and guild-access operations)
 *   - `discordUserId`  (`PlayerAdd`, `GrantGuildAccess`, `GuildMembers`
 *     response, etc.)
 *   - `ownerDiscordId` (response field on `MyGuild` — the **owner** role
 *     uses a snowflake shape distinct from `discordGuildId`, so it needs to
 *     be explicitly named here to survive truncation)
 *   - `officerRoleDiscordId` (response field on `MyGuild` — the configured
 *     Discord role used to grant officer access)
 *
 * DB-side ids (`playerId`, `groupId`, `roomId`, …) are small auto-increment
 * integers and never need this treatment.
 */
export const SNOWFLAKE_VAR_PATTERN =
  /"(discordGuildId|guildId|discordUserId|channelId)":"(\d+)"/g
export const SNOWFLAKE_FIELD_PATTERN =
  /"(discordGuildId|discordUserId|ownerDiscordId|officerRoleDiscordId|raidPublishChannelId)":(\d{16,})/g
const INT_SNOWFLAKE_VARIABLE_DECLARATION_PATTERN =
  /\$(discordGuildId|guildId|discordUserId|channelId)\s*:\s*Int\b/g

function intSnowflakeVariableNames(body: string): ReadonlyArray<string> {
  return Array.from(
    body.matchAll(INT_SNOWFLAKE_VARIABLE_DECLARATION_PATTERN),
    (match) => match[1],
  ).filter((name): name is string => name !== undefined)
}

/**
 * Rewrite legacy GraphQL `Int` snowflake variables from string-form
 * (`"name":"<digits>"`) to bare JSON numeric literals (`"name":<digits>`).
 *
 * A newer operation may correctly declare the same field as `String!` (for
 * example the WWM-notify control plane). Inspect the operation declaration
 * before rewriting so those exact decimal strings remain quoted on the wire.
 * JSON numeric literals preserve all digits for the legacy `Int!` parser.
 */
export function rewriteSnowflakes(body: string): string {
  const intVariableNames = intSnowflakeVariableNames(body)
  if (intVariableNames.length === 0) return body
  return body.replace(SNOWFLAKE_VAR_PATTERN, (_match, name: string, digits: string) => {
    return intVariableNames.includes(name) ? `"${name}":${digits}` : _match
  })
}

/**
 * Rewrite a GraphQL **response** body: JSON numeric fields whose **name**
 * matches a snowflake key and whose **value** is ≥ 16 digits become JSON
 * strings.
 *
 * The 16-digit floor is the truncation boundary: `Number.MAX_SAFE_INTEGER`
 * is 15.95 digits, so any 16+ digit JSON number has already lost precision
 * through `JSON.parse` and re-quoting it as a string restores the original
 * digits **only** if the wire bytes themselves were intact. (They are —
 * the api serializes snowflakes as JSON numbers unmodified, see
 * `snowflake_serde`.) DB-side ids are auto-increments (≤ 10 digits today),
 * so they fall under the threshold and are left alone.
 */
export function rewriteSnowflakesInResponse(body: string): string {
  return body.replace(
    SNOWFLAKE_FIELD_PATTERN,
    (_match, name: string, digits: string) => {
      return `"${name}":"${digits}"`
    },
  )
}
